import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFetcher, useRevalidator } from "react-router";

import {
  GUEST_LEARNING_CENTER_KEY,
  getGuestLearningData,
  getLearningCenterConsent,
  mergeLearningCenterData,
  saveGuestLearningData,
  saveLearningCenterConsent,
} from "~/hooks/learning-center/guest-learning-data";
import { clearVisitedLessons } from "~/hooks/learning-center/guest-lesson-tracker";
import {
  EMPTY_LEARNING_CENTER_DATA,
  type Goal,
  type LearningCenterData,
  type Level,
  type Module,
  type Quiz,
  type UpdatedDataResponse,
} from "~/hooks/learning-center/types";
import { useRootLayoutData } from "~/root";
import { useOnFetcherSettled } from "~/routes/resources/hooks/use-fetcher-settled";
import type { action } from "~/routes/resources/learning-center";

const RESOURCE_URL = "/resources/learning-center";

interface UseLearningCenterReturn {
  learningCenterData: NonNullable<LearningCenterData>;
  isLoading: boolean;
  error: Error | null;
  updateData: (reqBody: LearningCenterData) => Promise<UpdatedDataResponse>;
  confirmUpdateData: (
    reqBody: LearningCenterData,
  ) => Promise<UpdatedDataResponse>;
  updateConsentData: (consent: boolean) => Promise<UpdatedDataResponse>;
  getGoalByID: (goalID: string, data?: LearningCenterData) => Goal | null;
  getActiveGoal: (data?: LearningCenterData) => Goal | null;
  getLevelByID: (levelID: string, data?: LearningCenterData) => Level | null;
  getModuleByID: (moduleID: string, data?: LearningCenterData) => Module | null;
  getLessonByID: (
    lessonID: string,
    data?: LearningCenterData,
  ) => { id: string; complete?: string } | null;
  getQuizByModuleID: (
    moduleID: string,
    data?: LearningCenterData,
  ) => Quiz | null;
}

/**
 * Learning Center (LMS) progress hook — ported from hec-composable
 * (react-query → RR7). Registered progress lives on the SCAPI customer
 * (`c_learningCenterData`, surfaced via the root loader); guests use
 * localStorage. Writes go through the `/resources/learning-center` action
 * (registered) or localStorage (guest), with OPTIMISTIC local state so a
 * lesson/quiz completion reflects instantly and reconciles when the root
 * loader revalidates. Guest data merges into the profile once on login.
 *
 * Drop-in compatible with the prior stub: returns `{ learningCenterData,
 * isLoading, error }` + getters (consumed by HeroToggleTextButton,
 * use-learning-center-nav-tracker). (SFCC-11502 / ARTICLES-11.)
 */
export default function useLearningCenter(): UseLearningCenterReturn {
  const { customer, isRegistered } = useRootLayoutData();
  const fetcher = useFetcher<typeof action>();
  const { revalidate } = useRevalidator();

  // Guest progress: seed empty for SSR parity, then hydrate from localStorage
  // after mount (avoids a hydration mismatch — same approach as
  // `useGuestSaveForLater`) and re-sync on cross-tab writes.
  const [guestData, setGuestData] = useState<NonNullable<LearningCenterData>>(
    () => ({ ...EMPTY_LEARNING_CENTER_DATA }),
  );
  useEffect(() => {
    setGuestData(getGuestLearningData());
    const onStorage = (e: StorageEvent) => {
      if (e.key === GUEST_LEARNING_CENTER_KEY || e.key === null) {
        setGuestData(getGuestLearningData());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Optimistic overlay (registered writes only): cleared once the write settles,
  // by which point RR7 has revalidated the root loader → `serverData` is truth.
  const [optimistic, setOptimistic] =
    useState<NonNullable<LearningCenterData> | null>(null);

  // Parse the registered customer's data inside the memo (no setState-in-render):
  // surface any parse failure as a returned `error` rather than a side effect.
  const { data: serverData, error } = useMemo<{
    data: NonNullable<LearningCenterData>;
    error: Error | null;
  }>(() => {
    const raw = (customer as { c_learningCenterData?: string } | null)
      ?.c_learningCenterData;
    if (!raw) return { data: { ...EMPTY_LEARNING_CENTER_DATA }, error: null };
    try {
      const parsed = JSON.parse(raw) as LearningCenterData;
      const data =
        parsed && Array.isArray(parsed.goals)
          ? (parsed as NonNullable<LearningCenterData>)
          : { ...EMPTY_LEARNING_CENTER_DATA };
      return { data, error: null };
    } catch (e) {
      return {
        data: { ...EMPTY_LEARNING_CENTER_DATA },
        error: new Error(
          `Failed to parse c_learningCenterData JSON: ${
            e instanceof Error ? e.message : String(e)
          }`,
        ),
      };
    }
  }, [customer]);

  const sourceData = isRegistered ? serverData : guestData;
  const learningCenterData = optimistic ?? sourceData;

  // Drop the optimistic overlay once a registered write settles — by then RR7
  // has revalidated the root loader, so `serverData` is authoritative again.
  useOnFetcherSettled(fetcher, () => setOptimistic(null));

  const updateData = useCallback(
    (reqBody: LearningCenterData): Promise<UpdatedDataResponse> => {
      const next = structuredClone(
        reqBody ?? { ...EMPTY_LEARNING_CENTER_DATA },
      ) as NonNullable<LearningCenterData>;

      if (!isRegistered) {
        try {
          // Guest writes update `guestData` (the guest source) directly — no
          // optimistic overlay needed (there's no async round-trip to await).
          saveGuestLearningData(next);
          setGuestData(next);
          return Promise.resolve({ success: true, data: JSON.stringify(next) });
        } catch (e) {
          return Promise.resolve({
            success: false,
            errorMsg: "Failed to save to localStorage",
            errorDetail: e,
          });
        }
      }

      // Mirror composable: stamp the latest consent onto every registered write.
      next.tpaLearningCenterConsentData = getLearningCenterConsent();
      setOptimistic(next);
      // Resource routes are absolute + not locale-segmented in this storefront,
      // so the constant path is correct without `useURL` — and critically, this
      // hook is also invoked by the global nav-tracker which renders ABOVE the
      // SiteContext provider, where `useURL` would throw.
      fetcher.submit(
        { data: JSON.stringify(next) },
        { action: RESOURCE_URL, method: "post" },
      );
      // Optimistic: callers don't need to await the round-trip.
      return Promise.resolve({ success: true, data: JSON.stringify(next) });
    },
    [isRegistered, fetcher],
  );

  // Server-CONFIRMED write for deliberate, user-driven progress actions (the
  // "Continue: Lesson/Quiz" CTA). Unlike `updateData` (optimistic +
  // fire-and-forget), this AWAITS the SCAPI round-trip and then revalidates the
  // root loader so `customer`/`serverData` reflect the write. The LC fetcher
  // POST does NOT trigger root revalidation (root `shouldRevalidate` returns
  // false for it), so without an explicit revalidate the confirmed completion
  // would fall back to stale `serverData` on the next render. Resolves only
  // after the server responds. (SFCC-11738 — wait-for-confirmation + toast.)
  const confirmUpdateData = useCallback(
    async (reqBody: LearningCenterData): Promise<UpdatedDataResponse> => {
      const next = structuredClone(
        reqBody ?? { ...EMPTY_LEARNING_CENTER_DATA },
      ) as NonNullable<LearningCenterData>;

      // Guests own their data in localStorage — same synchronous path as
      // `updateData` (no round-trip, no revalidate; the caller skips the toast).
      if (!isRegistered) {
        try {
          saveGuestLearningData(next);
          setGuestData(next);
          return { success: true, data: JSON.stringify(next) };
        } catch (e) {
          return {
            success: false,
            errorMsg: "Failed to save to localStorage",
            errorDetail: e,
          };
        }
      }

      next.tpaLearningCenterConsentData = getLearningCenterConsent();
      const body = new FormData();
      body.set("data", JSON.stringify(next));
      try {
        const res = await fetch(RESOURCE_URL, {
          method: "post",
          body,
          // Cap the wait so a hung upstream SCAPI write can't strand the
          // awaiting CTA forever. 8s: `updateCustomer` can exceed the 2.5s used
          // for read-class calls; too short risks a false error on a slow but
          // committed write.
          signal: AbortSignal.timeout(8000),
        });
        const result = (await res.json()) as UpdatedDataResponse;
        if (result.success) {
          // Server is now source of truth: drop any stale optimistic overlay
          // (e.g. from a concurrent on-expand markOnly) and pull the persisted
          // data into the root loader. A revalidate failure must not poison an
          // already-persisted write.
          setOptimistic(null);
          try {
            await revalidate();
          } catch {
            /* persisted; the UI reconciles on the next navigation */
          }
        }
        return result;
      } catch (e) {
        return {
          success: false,
          errorMsg: "Failed to persist learning center data",
          errorDetail: e,
        };
      }
    },
    [isRegistered, revalidate],
  );

  const updateConsentData = useCallback(
    (consent: boolean): Promise<UpdatedDataResponse> => {
      saveLearningCenterConsent(consent);
      return updateData({
        ...(learningCenterData ?? EMPTY_LEARNING_CENTER_DATA),
        tpaLearningCenterConsentData: consent,
      });
    },
    [updateData, learningCenterData],
  );

  // --- Guest → profile merge, once on login (idempotent via `hasRegistered`).
  const mergedRef = useRef(false);
  useEffect(() => {
    if (mergedRef.current || !isRegistered) return;
    const guest = getGuestLearningData();
    if (guest.hasRegistered || !(guest.goals?.length > 0)) return;

    mergedRef.current = true;
    saveGuestLearningData({ ...guest, hasRegistered: true });
    const merged = mergeLearningCenterData(
      serverData,
      guest,
      getLearningCenterConsent(),
    );
    void updateData(merged);
    // The 2-lesson guest cap is localStorage-only and never merges into the
    // profile — reset it on registration so the new account starts uncapped.
    // (Composable also calls a phantom `clearCompletedLessons()` here; that
    // helper is undefined, so it is intentionally not ported.)
    clearVisitedLessons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRegistered, serverData]);

  // --- Getters (pure; default to the resolved `learningCenterData`).
  const getGoalByID = useCallback(
    (goalID: string, data?: LearningCenterData): Goal | null => {
      const lc = data ?? learningCenterData;
      return lc?.goals ? (lc.goals.find(g => g.id === goalID) ?? null) : null;
    },
    [learningCenterData],
  );

  const getActiveGoal = useCallback(
    (data?: LearningCenterData): Goal | null => {
      const lc = data ?? learningCenterData;
      if (!lc?.activeGoal) return null;
      return getGoalByID(lc.activeGoal, lc);
    },
    [learningCenterData, getGoalByID],
  );

  const getLevelByID = useCallback(
    (levelID: string, data?: LearningCenterData): Level | null => {
      const lc = data ?? learningCenterData;
      if (!lc) return null;
      return (
        lc.goals.flatMap(g => g.levels).find(l => l.id === levelID) ?? null
      );
    },
    [learningCenterData],
  );

  const getModuleByID = useCallback(
    (moduleID: string, data?: LearningCenterData): Module | null => {
      const lc = data ?? learningCenterData;
      if (!lc) return null;
      return (
        lc.goals
          .flatMap(g => g.levels.flatMap(l => l.modules))
          .find(m => m.id === moduleID) ?? null
      );
    },
    [learningCenterData],
  );

  const getLessonByID = useCallback(
    (lessonID: string, data?: LearningCenterData) => {
      const lc = data ?? learningCenterData;
      if (!lc) return null;
      return (
        lc.goals
          .flatMap(g =>
            g.levels.flatMap(l => l.modules.flatMap(m => m.lessons ?? [])),
          )
          .find(lesson => lesson?.id === lessonID) ?? null
      );
    },
    [learningCenterData],
  );

  const getQuizByModuleID = useCallback(
    (moduleID: string, data?: LearningCenterData): Quiz | null => {
      const lc = data ?? learningCenterData;
      if (!lc) return null;
      return getModuleByID(moduleID, lc)?.quiz ?? null;
    },
    [learningCenterData, getModuleByID],
  );

  return {
    learningCenterData,
    isLoading: false, // customer is resolved by the root loader before render
    error,
    updateData,
    confirmUpdateData,
    updateConsentData,
    getGoalByID,
    getActiveGoal,
    getLevelByID,
    getModuleByID,
    getLessonByID,
    getQuizByModuleID,
  };
}
